import React from 'react';
import ReactECharts from 'echarts-for-react';
import SafeIcon from '../common/SafeIcon';

const ReportStats = () => {
  // Chart 1: Caller Behavior when unanswered
  const behaviorOptions = {
    backgroundColor: 'transparent',
    tooltip: { trigger: 'item', backgroundColor: '#1e293b', textStyle: { color: '#f8fafc' }, borderColor: '#334155' },
    series: [
      {
        name: 'Caller Action',
        type: 'pie',
        radius: ['50%', '80%'],
        avoidLabelOverlap: false,
        itemStyle: { borderRadius: 8, borderColor: '#0f172a', borderWidth: 4 },
        label: { show: false },
        data: [
          { value: 85, name: 'Hang Up / Call Competitor', itemStyle: { color: '#ef4444' } },
          { value: 15, name: 'Leave Voicemail', itemStyle: { color: '#10b981' } }
        ]
      }
    ]
  };

  // Chart 2: Missed Call Rate by Time of Day
  const timeOptions = {
    backgroundColor: 'transparent',
    tooltip: { trigger: 'axis', backgroundColor: '#1e293b', textStyle: { color: '#f8fafc' } },
    grid: { left: '5%', right: '5%', bottom: '10%', top: '15%', containLabel: true },
    xAxis: { 
      type: 'category', 
      data: ['8AM', '12PM', '4PM', '8PM', '12AM'], 
      axisLabel: { color: '#94a3b8' },
      axisLine: { lineStyle: { color: '#334155' } }
    },
    yAxis: { 
      type: 'value', 
      axisLabel: { color: '#94a3b8', formatter: '{value}%' },
      splitLine: { lineStyle: { color: '#1e293b' } }
    },
    series: [
      {
        data: [15, 45, 25, 85, 95],
        type: 'line',
        smooth: true,
        lineStyle: { width: 4, color: '#3b82f6' },
        areaStyle: {
          color: {
            type: 'linear', x: 0, y: 0, x2: 0, y2: 1,
            colorStops: [
              { offset: 0, color: 'rgba(59, 130, 246, 0.5)' },
              { offset: 1, color: 'rgba(59, 130, 246, 0.0)' }
            ]
          }
        },
        symbol: 'circle',
        symbolSize: 8,
        itemStyle: { color: '#60a5fa' }
      }
    ]
  };

  return (
    <section className="py-24 bg-slate-950 border-y border-slate-800 relative overflow-hidden">
      {/* Background glow */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-4xl h-full bg-blue-900/10 blur-[120px] pointer-events-none"></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-900/30 border border-blue-800/50 mb-6">
            <SafeIcon name="FileText" className="text-blue-400" />
            <span className="text-sm font-medium text-blue-300">Official Report: Quantifying Missed Business Calls & Revenue Loss</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">The Hard Data on Missed Calls</h2>
          <p className="text-slate-400 max-w-2xl mx-auto">
            Industry studies reveal exactly what happens when a customer hears a ringing line instead of a human voice. The results directly impact your bottom line.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12">
          {/* Chart 1 Container */}
          <div className="bg-slate-900/80 border border-slate-800 rounded-3xl p-8 shadow-xl">
            <h3 className="text-xl font-bold text-white mb-2">Caller Behavior on Unanswered Calls</h3>
            <p className="text-sm text-slate-400 mb-6">Voicemail is dead. The vast majority of callers will instantly seek out your competitor.</p>
            <div className="h-[300px] w-full relative">
              <ReactECharts option={behaviorOptions} style={{ height: '100%', width: '100%' }} />
              {/* Center text for donut chart */}
              <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                <span className="text-4xl font-extrabold text-white">85%</span>
                <span className="text-xs text-slate-400 font-medium uppercase tracking-wider">Abandon</span>
              </div>
            </div>
            <div className="flex justify-center gap-6 mt-4">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-red-500"></div>
                <span className="text-sm text-slate-300">Call Competitor</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-emerald-500"></div>
                <span className="text-sm text-slate-300">Leave Voicemail</span>
              </div>
            </div>
          </div>

          {/* Chart 2 Container */}
          <div className="bg-slate-900/80 border border-slate-800 rounded-3xl p-8 shadow-xl">
            <h3 className="text-xl font-bold text-white mb-2">Missed Call Rate by Time of Day</h3>
            <p className="text-sm text-slate-400 mb-6">Over 40% of inbound revenue opportunities happen outside of standard 9-5 operating hours or during lunch rushes.</p>
            <div className="h-[300px] w-full">
              <ReactECharts option={timeOptions} style={{ height: '100%', width: '100%' }} />
            </div>
            <div className="mt-4 p-4 bg-blue-900/20 rounded-xl border border-blue-800/30">
              <p className="text-sm text-blue-200 flex items-start gap-3">
                <SafeIcon name="Info" className="text-blue-400 text-lg shrink-0 mt-0.5" />
                <span>The average cost of a single missed call in the local service sector is estimated at <strong>$250+ in lost lifetime value</strong>.</span>
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ReportStats;